import { memo, useCallback, useMemo } from 'react';

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { DATE_ID_FORMAT, DATE_KEY_FORMAT } from '@/helpers';
import { mapTeamMemberStatusFromDTO } from '@/modules/common/mappers/team-member-status.mapper';
import { MemberStatus } from '@/modules/team';

import { HIDDEN_STATE_CHANGE_FIELDS, REM_TO_PX } from '../../constants';
import { getStateChangeForDate, isCompactCellWidth } from '../../helpers';
import {
  AssignmentFragment,
  buildAssignmentFragments,
  buildTimeOffPlacementLookup,
  isAssignmentDateCutByApprovedTimeOff,
  splitFragmentsAroundTimeOffs,
} from '../../helpers/assignment-timeoff-fragments';
import {
  buildTimeOffSegments,
  DEFAULT_CAPACITY_MINUTES,
} from '../../helpers/time-off-day-stacking';
import { useAddAssignment } from '../../hooks';
import usePlannerStore from '../../store';
import { AssignmentType, RowItem, TimeOffBlockType } from '../../types';
import { PlannerUserStateChangeResponse } from '../../types/dto';
import { Assignment } from '../assignment/assignment';
import { PhantomAssignment } from '../assignment/phantom-assignment';
import { StickyTimeOffLabels } from '../time-off/text/sticky-time-off-labels';
import { TimeOffBlock } from '../time-off/time-off-block';
import { StateChangeIndicator } from '../user-change/state-change-indicator';
import { TimelineCell } from './timeline-cell';
import { TimelineSpacer } from './timeline-spacer';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface TimelineRowOverlayProps {
  row: RowItem;
  assignments: AssignmentType[];
  timeOffs: TimeOffBlockType[];
  assignmentLevels: Map<number, number>;
  cellWidth: number;
  cellHeight: number;
  userStartDate: dayjs.Dayjs | undefined;
  dates: dayjs.Dayjs[];
  visibleDates: dayjs.Dayjs[];
  leftSpacerWidth: number;
  rightSpacerWidth: number;
  /** Notifies which time-off segments have an active sticky label (so the in-block
   *  ScrollableText is hidden for them). Forwarded to the StickyTimeOffLabels
   *  rendered inside this overlay (between blocks and assignments) so the label
   *  sits ABOVE the block but BELOW the assignment bars. */
  onStickyTimeOffSegmentsChange: (segments: Map<number, Set<string>>) => void;
}

/**
 * Overlay component containing all heavy interactive content for timeline rows.
 * Only rendered after idle callback (phase 2 of deferred rendering).
 * Includes utilization cells, assignments, phantom assignments, and state change indicators.
 */
export const TimelineRowOverlay = memo(
  ({
    row,
    assignments,
    timeOffs,
    assignmentLevels,
    cellWidth,
    cellHeight,
    userStartDate,
    dates,
    visibleDates,
    leftSpacerWidth,
    rightSpacerWidth,
    onStickyTimeOffSegmentsChange,
  }: TimelineRowOverlayProps) => {
    // Compact display (dots, single-letter weekdays, tight padding) is derived from
    // the current cell width — it scales smoothly with zoom in both directions.
    const isCompact = isCompactCellWidth(cellWidth);

    // Phase 2 store subscription — only mounted after idle
    const utilizationBasedOn = usePlannerStore(
      store => store.plannerPreferences.utilizationBasedOn
    );

    const {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseEnter,
      onMouseLeave,
      timelineRef,
      isHovering,
      isSelecting,
      hoveredDayIndex,
      hoveredLevel,
      startDayIndex,
      endDayIndex,
    } = useAddAssignment(row, userStartDate);

    const assignmentFragments = useMemo<AssignmentFragment[]>(
      () => buildAssignmentFragments(assignments),
      [assignments]
    );

    // Per-date capacity resolver (minutes/day) — used both for time-off block
    // sizing and for detecting approved full-capacity days that fully cut the
    // assignment (FULL, or HOURLY ≥ capacity). Falls back to the member's nominal
    // day when the utilization capacity is missing OR zero: on a full-vacation day
    // the daily capacity can be reported as 0, which would otherwise hide nothing.
    const getCapacityMinutes = useCallback(
      (dateKey: string) => {
        const nominal = row.hoursPerDay > 0 ? row.hoursPerDay * 60 : DEFAULT_CAPACITY_MINUTES;
        const capacity = row.utilizationByDate?.get(dateKey)?.capacity;
        return capacity && capacity > 0 ? capacity : nominal;
      },
      [row.utilizationByDate, row.hoursPerDay]
    );

    // Each partial time-off day drops the assignment into that day's free zone
    // (stacked by level), computed against the ACTUAL cell height, which
    // TimelineRow already grew to fit that stack. Approved full-capacity days produce no
    // fragment. Consecutive same-position days merge; varying days split per-day.
    const renderableFragments = useMemo(
      () =>
        splitFragmentsAroundTimeOffs(
          assignmentFragments,
          assignmentLevels,
          timeOffs,
          cellHeight,
          getCapacityMinutes
        ),
      [assignmentFragments, assignmentLevels, timeOffs, cellHeight, getCapacityMinutes]
    );

    // Resolve each time-off into non-overlapping per-day segments. A date's
    // layout accounts for every time-off present on it (hourly from the bottom
    // by capacity, non-hourly tiling the region above). Uniform time-offs
    // render as a single bar; non-uniform ones split into per-day segments.
    // Blocks are sized as a portion of the ACTUAL content area, so they fill
    // their share of the current row height and recompute proportionally when
    // the row grows or shrinks (more/fewer assignment levels).
    const timeOffSegments = useMemo(
      () => buildTimeOffSegments(timeOffs, getCapacityMinutes, cellHeight),
      [timeOffs, getCapacityMinutes, cellHeight]
    );

    // Time-off placement lookup for the row, used to decide whether a cell is
    // truly empty. Only APPROVED time-offs fully cut the assignment; over a
    // pending time-off the bar is still present, so the cell counts as occupied.
    const timeOffPlacementLookup = useMemo(() => buildTimeOffPlacementLookup(timeOffs), [timeOffs]);

    // Calculate if the hovered cell is occupied by a visible assignment fragment
    const isHoveredCellOccupied = useMemo(() => {
      if (hoveredDayIndex === null || hoveredLevel === null) return false;

      const hoveredDate = dates[hoveredDayIndex];
      if (!hoveredDate) return false;

      const dateKey = hoveredDate.format(DATE_KEY_FORMAT);

      return assignments.some(assignment => {
        const assignmentLevel = assignmentLevels.get(assignment.originalAssignment.id);
        if (assignmentLevel !== hoveredLevel) return false;

        const inRange =
          hoveredDate.isSameOrAfter(assignment.startDate, 'day') &&
          hoveredDate.isSameOrBefore(assignment.endDate, 'day');
        if (!inRange) return false;

        // The cell is empty only where an APPROVED time-off fully cuts the
        // assignment out. Over a pending time-off the bar still occupies the cell.
        return !isAssignmentDateCutByApprovedTimeOff(
          assignment,
          dateKey,
          timeOffPlacementLookup,
          getCapacityMinutes
        );
      });
    }, [
      hoveredDayIndex,
      hoveredLevel,
      dates,
      assignments,
      assignmentLevels,
      timeOffPlacementLookup,
      getCapacityMinutes,
    ]);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (isHoveredCellOccupied) return;
      onMouseDown(e);
    };

    const dateIndexByKey = useMemo(
      () =>
        new Map<string, number>(
          dates.map((date, index) => [date.format(DATE_KEY_FORMAT), index] as const)
        ),
      [dates]
    );

    const stateChangeIndicators = useMemo(
      () =>
        (row.stateChanges || [])
          .map(stateChangeRaw => {
            const changeDateKey = dayjs(stateChangeRaw.effectiveStartDate).format(DATE_KEY_FORMAT);
            const dateIndex = dateIndexByKey.get(changeDateKey);
            if (dateIndex === undefined) return null;

            const position = dateIndex * cellWidth * REM_TO_PX;

            // Filter out hidden fields from the changed fields list
            const changedFields = stateChangeRaw.changedFields.filter(
              field => !HIDDEN_STATE_CHANGE_FIELDS.includes(field.fieldName)
            );

            // Only show state change indicator if:
            // 1. There are visible changed fields, OR
            // 2. It's an initial state (first time team member was added)
            const hasVisibleChanges = changedFields.length > 0;
            const isInitialState = stateChangeRaw.isInitialChange;

            if (!hasVisibleChanges && !isInitialState) return null;

            const stateChange = { ...stateChangeRaw, changedFields };

            return { stateChange, position, isInitialState };
          })
          .filter(
            (
              item
            ): item is {
              stateChange: PlannerUserStateChangeResponse;
              position: number;
              isInitialState: boolean;
            } => Boolean(item)
          ),
      [row.stateChanges, dateIndexByKey, cellWidth]
    );

    const fullCells = useMemo(
      () =>
        visibleDates.map(date => {
          const dateKey = date.format(DATE_KEY_FORMAT);
          const utilization = row.utilizationByDate?.get(dateKey);
          const utilizationPercentage = utilization?.percent ?? 0;
          const assigned = utilization?.assigned ?? 0;
          const capacity = utilization?.capacity ?? 0;
          const onDemand = utilization?.onDemand ?? false;

          const isUserCreated = !userStartDate || date.isSameOrAfter(userStartDate, 'day');
          const dateStateChange = getStateChangeForDate(row.stateChanges, date);
          const dateStatus = dateStateChange?.displayStatus
            ? mapTeamMemberStatusFromDTO(dateStateChange.displayStatus)
            : MemberStatus.Basic;
          const isArchived = dateStatus === MemberStatus.Archived;

          return (
            <TimelineCell
              key={`cell-${date.format(DATE_ID_FORMAT)}`}
              date={date}
              cellWidth={cellWidth}
              height={cellHeight}
              utilizationPercentage={utilizationPercentage}
              assigned={assigned}
              capacity={capacity}
              onDemand={onDemand}
              isCompact={isCompact}
              disabled={!isUserCreated || isArchived}
              utilizationBasedOn={utilizationBasedOn}
            />
          );
        }),
      [
        visibleDates,
        row.utilizationByDate,
        row.stateChanges,
        userStartDate,
        cellWidth,
        cellHeight,
        isCompact,
        utilizationBasedOn,
      ]
    );

    return (
      <>
        {/* Utilization cells layer — below assignments, pointer-events on tooltip text only */}
        <div className="pointer-events-none absolute inset-0 flex">
          <TimelineSpacer widthRem={leftSpacerWidth} height={cellHeight} />
          {fullCells}
          <TimelineSpacer widthRem={rightSpacerWidth} height={cellHeight} />
        </div>
        {/* Interactive content layer — above utilization cells */}
        <div
          id={`row-overlay-${row.id}`}
          className="absolute inset-0 z-[2] flex h-full w-full items-center"
          ref={timelineRef}
          onMouseDown={handleMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {stateChangeIndicators.map((indicator, index) => (
            <StateChangeIndicator
              key={`state-change-${indicator.stateChange.effectiveStartDate}-${index}`}
              stateChange={indicator.stateChange}
              leftPosition={indicator.position}
              height={cellHeight}
            />
          ))}
          {/* Phantom Assignment - shows on hover and extends during drag */}
          {((isHovering && hoveredDayIndex !== null) ||
            (isSelecting && startDayIndex !== null && endDayIndex !== null)) && (
            <PhantomAssignment
              startOffset={
                isSelecting && startDayIndex !== null && endDayIndex !== null
                  ? Math.min(startDayIndex, endDayIndex) * cellWidth
                  : (hoveredDayIndex ?? 0) * cellWidth
              }
              endOffset={
                isSelecting && startDayIndex !== null && endDayIndex !== null
                  ? (Math.abs(endDayIndex - startDayIndex) + 1) * cellWidth
                  : cellWidth
              }
              level={hoveredLevel}
              isVisible={!isHoveredCellOccupied}
              isDragging={isSelecting}
              isCompact={isCompact}
              cellWidth={cellWidth}
            />
          )}
          {/* Time-offs are rendered BEFORE assignments so that, within this single
              stacking context, the assignment bar (explicit z-1, rendered later)
              wins the stacking tie over BOTH a PENDING time-off (z-0) and an
              APPROVED one (z-[1]). The bar stays clickable/draggable wherever it
              physically sits — including partial approved days (half/quarter/custom)
              where it's reduced + repositioned rather than cut — while the time-off
              block still catches hover everywhere the bar doesn't cover. */}
          {timeOffSegments.map(segment => (
            <TimeOffBlock
              key={segment.key}
              segment={segment}
              user={row.user}
              dates={dates}
              cellWidth={cellWidth}
            />
          ))}
          {/* Time-off sticky labels render here — AFTER the blocks (so they paint
              on top of the stripe) but BEFORE the assignment bars (so the bars and
              their text win on overlap). */}
          <StickyTimeOffLabels
            timeOffs={timeOffs}
            cellWidth={cellWidth}
            cellHeight={cellHeight}
            getCapacityMinutes={getCapacityMinutes}
            dates={dates}
            onStickySegmentsChange={onStickyTimeOffSegmentsChange}
          />
          {renderableFragments.map(fragment => (
            <Assignment
              key={fragment.key}
              {...fragment.assignment}
              startDate={fragment.startDate}
              endDate={fragment.endDate}
              user={row.user}
              stateChanges={row.stateChanges}
              userStartDate={userStartDate}
              style={{
                position: 'absolute',
                top: fragment.top,
              }}
            />
          ))}
        </div>
      </>
    );
  }
);
